import classNames from "classnames";
import { useFormik } from "formik";
import { filter, find, get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { FaRegCircleCheck } from "react-icons/fa6";
import { RxCross2 } from "react-icons/rx";
import * as Yup from "yup";
import Button from "shared/components/Button";
import CallButton from "shared/components/CallButton";
import Input from "shared/components/Input";
import PhoneWithPrefix from "shared/components/Input/PhoneWithPrefix";
import {
  CLIENT_PHONE_PREFIXES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  POLYNESIA_PHONE_PREFIX,
  RELATIONSHIP,
} from "utils/constants";
import {
  createClientPhoneSchema,
  getEditablePhoneFields,
  getPhoneFromTelecoms,
} from "utils/helpers/phone";

const Information = ({
  data,
  project,
  translate,
  onUpdateEmail,
  onUpdatePhone,
}) => {
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  const children = filter(get(data, "relatedPersons", []), {
    relationship: RELATIONSHIP.CHILD,
  });

  const spouse = get(data, "relatedPersons", [])?.find(
    ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
  );

  useEffect(() => {
    setIsEditingEmail(false);
    setIsEditingPhone(false);
  }, [data]);

  const formik = useFormik({
    initialValues: {
      email: get(
        find(get(data, "telecoms", []), {
          system: CONTACT_POINT_SYSTEM.EMAIL,
        }),
        "value",
        "",
      ),
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(translate.INVALID_EMAIL)
        .required(translate.REQUIRED_EMAIL),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onUpdateEmail(values.email);
        setIsEditingEmail(false);
      } catch {
        // Keep edit mode open so the user can retry after a failed update.
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const phoneFields = getEditablePhoneFields(
    get(data, "telecoms", []),
    CLIENT_PHONE_PREFIXES,
  );
  const displayedPhone = getPhoneFromTelecoms(get(data, "telecoms", [])) || "";
  const showPrefixInput = phoneFields.hasStoredPrefix;

  const phoneFormik = useFormik({
    initialValues: {
      phone: phoneFields.localNumber,
      phonePrefix: phoneFields.prefix,
    },
    validationSchema: createClientPhoneSchema(translate),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onUpdatePhone({
          phone: values.phone,
          phonePrefix: values.phonePrefix,
        });
        setIsEditingPhone(false);
      } catch {
        // Keep edit mode open so the user can retry after a failed update.
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const commitPhoneValue = async (nextPhone) => {
    await phoneFormik.setFieldValue("phone", nextPhone);
  };

  const markPhoneTouched = () => {
    phoneFormik.setFieldTouched("phone", true);
  };

  return (
    <div className="contact__drawer--container">
      <div className="contact__drawer--container__child">
        <div className="contact__drawer--container__child--header">
          <span>{translate.ADHERENT}</span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(data, "user.lastname"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.LAST_NAME}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {get(data, "user.lastname")}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(data, "user.firstname"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.FIRST_NAME}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {get(data, "user.firstname")}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "tracer.name"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.SOURCE}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {get(project, "tracer.name")}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "fields.contactType"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.LEAD_TYPE}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {get(project, "fields.contactType")}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "fields.weedoTrust.scoreTotal"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            <span>{translate.TOTAL_SCORE} : </span>
            <span>{get(project, "fields.weedoTrust.scoreTotal")}</span>
          </span>
          <span className="contact__drawer--container__child--info__content">
            {translate.TOTAL_SCORE_DESCRIPTION}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "fields.weedoTrust.scoreEmail"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            <span>{translate.EMAIL_SCORE} : </span>
            <span>{get(project, "fields.weedoTrust.scoreEmail")}</span>
          </span>
          <span className="contact__drawer--container__child--info__content">
            {translate.EMAIL_SCORE_DESCRIPTION}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "fields.weedoTrust.scorePhone"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            <span>{translate.PHONE_SCORE} : </span>
            <span>{get(project, "fields.weedoTrust.scorePhone")}</span>
          </span>
          <span className="contact__drawer--container__child--info__content">
            {translate.PHONE_SCORE_DESCRIPTION}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "fields.weedoTrust.scoreActivity"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            <span>{translate.SCORE_ACTIVITY} : </span>
            <span>{get(project, "fields.weedoTrust.scoreActivity")}</span>
          </span>
          <span className="contact__drawer--container__child--info__content">
            {translate.SCORE_ACTIVITY_DESCRIPTION}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "fields.weedoTrust.currentNetwork"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            <span>{translate.CURRENT_NETWORK} : </span>
            <span
              className={classNames({
                green:
                  get(project, "fields.weedoTrust.currentNetwork") === "true",
                orange:
                  get(project, "fields.weedoTrust.currentNetwork") === "false",
              })}
            >
              {get(project, "fields.weedoTrust.currentNetwork")}
            </span>
          </span>
          <span className="contact__drawer--container__child--info__content">
            {translate.CURRENT_NETWORK_DESCRIPTION}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(data, "user.birthDate"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.BIRTH_DATE}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {moment(get(data, "user.birthDate")).format(DATE_FORMAT)}
          </span>
        </div>
        <div className={classNames("contact__drawer--container__child--info")}>
          <span className="contact__drawer--container__child--info__title">
            {translate.EMAIL}
          </span>
          <span className="contact__drawer--container__child--info__content email__edit">
            {isEditingEmail ? (
              <form
                onSubmit={formik.handleSubmit}
                className="email__edit--wrapper"
              >
                <Button
                  type="gray--outlined"
                  onClick={() => setIsEditingEmail(false)}
                  className="cancel-btn"
                >
                  <BsArrowLeft />
                </Button>
                <Input
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldTouched("email", true);
                  }}
                  onBlur={formik.handleBlur}
                  className="email-input"
                  errors={formik.errors.email}
                  touched={formik.touched.email}
                />
                <Button
                  type="primary"
                  className="update-btn"
                  onClick={formik.handleSubmit}
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  {translate.UPDATE}
                </Button>
              </form>
            ) : (
              <div className="email__edit--display">
                {formik.values.email || "-"}
                <Button
                  type="primary--underline"
                  htmlType="button"
                  onClick={() => setIsEditingEmail(true)}
                >
                  {translate.EDIT}
                </Button>
              </div>
            )}
          </span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(
              find(get(data, "telecoms", []), {
                system: CONTACT_POINT_SYSTEM.ZIP_CODE,
              }),
              "value",
            ),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.ZIP_CODE}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {get(
              find(get(data, "telecoms", []), {
                system: CONTACT_POINT_SYSTEM.ZIP_CODE,
              }),
              "value",
              "-",
            )}
          </span>
        </div>
        <div
          className={classNames(
            "contact__drawer--container__child--info",
            "contact__drawer--container__child--info--phone-row",
          )}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.PHONE}
          </span>
          <span className="contact__drawer--container__child--info__content email__edit">
            {isEditingPhone ? (
              <form
                onSubmit={phoneFormik.handleSubmit}
                className="email__edit--wrapper"
              >
                <Button
                  type="gray--outlined"
                  onClick={() => {
                    phoneFormik.resetForm();
                    setIsEditingPhone(false);
                  }}
                  className="cancel-btn"
                >
                  <BsArrowLeft />
                </Button>
                {showPrefixInput ? (
                  <PhoneWithPrefix
                    prefixOptions={CLIENT_PHONE_PREFIXES}
                    prefixValue={phoneFormik.values.phonePrefix}
                    value={phoneFormik.values.phone}
                    placeholder={
                      phoneFormik.values.phonePrefix === POLYNESIA_PHONE_PREFIX
                        ? translate.PHONE_PLACEHOLDER_POLYNESIA
                        : translate.PHONE_PLACEHOLDER
                    }
                    errors={phoneFormik.errors.phone}
                    touched={phoneFormik.touched.phone}
                    onChange={commitPhoneValue}
                    onPrefixChange={async (prefix) => {
                      await phoneFormik.setFieldValue("phonePrefix", prefix);
                      await phoneFormik.validateField("phone");
                    }}
                    onBlur={markPhoneTouched}
                  />
                ) : (
                  <Input
                    type="phone"
                    name="phone"
                    value={phoneFormik.values.phone}
                    placeholder={translate.PHONE_PLACEHOLDER}
                    onChange={(e) => commitPhoneValue(e.target.value)}
                    onBlur={markPhoneTouched}
                    className="email-input"
                    errors={phoneFormik.errors.phone}
                    touched={phoneFormik.touched.phone}
                  />
                )}
                <Button
                  type="primary"
                  className="update-btn"
                  onClick={phoneFormik.handleSubmit}
                  disabled={!phoneFormik.isValid || phoneFormik.isSubmitting}
                >
                  {translate.UPDATE}
                </Button>
              </form>
            ) : (
              <div className="email__edit--display d-flex align--center gap-8">
                {displayedPhone || "-"}
                {displayedPhone && (
                  <CallButton
                    phoneNumber={displayedPhone}
                    contactId={get(data, "id")}
                    projectId={get(project, "id")}
                    getPopupContainer={(trigger) =>
                      trigger?.closest(".ant-drawer-content") || document.body
                    }
                    buttonClassName="call-button--inline"
                  />
                )}
                <Button
                  type="primary--underline"
                  htmlType="button"
                  onClick={() => {
                    phoneFormik.resetForm();
                    setIsEditingPhone(true);
                  }}
                >
                  {translate.EDIT}
                </Button>
              </div>
            )}
          </span>
        </div>
        {[CONTACT_POINT_SYSTEM.LANDLINE].map((system) => {
          const telecoms = get(data, "telecoms", []);
          const value = getPhoneFromTelecoms(telecoms, system);
          if (!value) return null;

          return (
            <div
              key={system}
              className={classNames(
                "contact__drawer--container__child--info",
                "contact__drawer--container__child--info--phone-row",
              )}
            >
              <span className="contact__drawer--container__child--info__title">
                {translate.LANDLINE}
              </span>
              <span className="contact__drawer--container__child--info__content d-flex align--center gap-8">
                {value}
                <CallButton
                  phoneNumber={value}
                  contactId={get(data, "id")}
                  projectId={get(project, "id")}
                  getPopupContainer={(trigger) =>
                    trigger?.closest(".ant-drawer-content") || document.body
                  }
                  buttonClassName="call-button--inline"
                />
              </span>
            </div>
          );
        })}
      </div>
      <div
        className={classNames("contact__drawer--container__child--info", {
          hidden: !get(data, "fields.profile"),
        })}
      >
        <span className="contact__drawer--container__child--info__title">
          {translate.PROFILE}
        </span>
        <span className="contact__drawer--container__child--info__content">
          {translate.PROFILES[get(data, "fields.profile")] ||
            get(data, "fields.profile", "-")}
        </span>
      </div>
      <div
        className={classNames("contact__drawer--container__child--info", {
          hidden: get(data, "fields.isClient") === undefined,
        })}
      >
        <span className="contact__drawer--container__child--info__title">
          {translate.ALREADY_CLIENT}
        </span>
        <span className="contact__drawer--container__child--info__content">
          {get(data, "fields.isClient", false) ? (
            <FaRegCircleCheck size={24} color="#41AD6A" />
          ) : (
            <RxCross2 size={24} color="#EB5757" />
          )}
        </span>
      </div>
      <div
        className={classNames("contact__drawer--container__child--info", {
          hidden: get(project, "fields.info.cssBeneficiary") === undefined,
        })}
      >
        <span className="contact__drawer--container__child--info__title">
          {translate.CSS_CLIENT}
        </span>
        <span className="contact__drawer--container__child--info__content">
          {get(project, "fields.info.cssBeneficiary", false) ? (
            <FaRegCircleCheck size={24} color="#41AD6A" />
          ) : (
            <RxCross2 size={24} color="#EB5757" />
          )}
        </span>
      </div>
      <div
        className={classNames("contact__drawer--container__child--info", {
          hidden: get(project, "fields.info.fonctionnaryOrAgent") === undefined,
        })}
      >
        <span className="contact__drawer--container__child--info__title">
          {translate.TERRITORIAL_AGENT}
        </span>
        <span className="contact__drawer--container__child--info__content">
          {get(project, "fields.info.fonctionnaryOrAgent", false) ? (
            <FaRegCircleCheck size={24} color="#41AD6A" />
          ) : (
            <RxCross2 size={24} color="#EB5757" />
          )}
        </span>
      </div>
      <div
        className={classNames("contact__drawer--container__child", {
          hidden: !get(project, "fields.graCode"),
        })}
      >
        <div className="contact__drawer--container__child--header">
          <span>{translate.CLIENT_SPECIFIC_FIELDS}</span>
        </div>
        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !get(project, "fields.graCode"),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.GRA_CODE}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {get(project, "fields.graCode", "-")}
          </span>
        </div>
      </div>
      <div
        className={classNames("contact__drawer--container__child", {
          hidden: isEmpty(spouse),
        })}
      >
        <div className="contact__drawer--container__child--header">
          <span>{translate.SPOUSE}</span>
          {spouse ? (
            <FaRegCircleCheck size={20} color="#41AD6A" />
          ) : (
            <RxCross2 size={20} color="#EB5757" />
          )}
        </div>
        {spouse && (
          <div
            className={classNames("contact__drawer--container__child--info", {
              hidden: !get(spouse, "identity.user.birthDate"),
            })}
          >
            <span className="contact__drawer--container__child--info__title">
              {translate.SPOUSE_BIRTH_DATE}
            </span>
            <span className="contact__drawer--container__child--info__content">
              {moment(get(spouse, "identity.user.birthDate")).format(
                DATE_FORMAT,
              )}
            </span>
          </div>
        )}
      </div>
      <div
        className={classNames("contact__drawer--container__child", {
          hidden: isEmpty(children),
        })}
      >
        <div className="contact__drawer--container__child--header">
          <span>{translate.CHILDREN}</span>
          {isEmpty(children) ? (
            <RxCross2 size={20} color="#EB5757" />
          ) : (
            <FaRegCircleCheck size={20} color="#41AD6A" />
          )}
        </div>

        <div
          className={classNames("contact__drawer--container__child--info", {
            hidden: !isEmpty(children),
          })}
        >
          <span className="contact__drawer--container__child--info__title">
            {translate.CHILDREN_COUNT}
          </span>
          <span className="contact__drawer--container__child--info__content">
            {children.length}
          </span>
        </div>
        {children.map((_, index) => (
          <div
            key={`child-${index}`}
            className={classNames("contact__drawer--container__child--info")}
          >
            <span className="contact__drawer--container__child--info__title">
              {translate.CHILDREN_BIRTH_DATE} {index + 1}
            </span>
            <span className="contact__drawer--container__child--info__content">
              {moment(get(_, "identity.user.birthDate")).format(DATE_FORMAT)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
export default Information;
